<template>
    <div>
        <base-dialog :loading="loading" :value="dialog" :width="560" :fullscreen="isMobile" :title="$t('common.evaluation_report')"
            @input="onInput" @close="hide">
            <template v-if="hasData && canShare" v-slot:header-actions>
                <button-tooltip bottom :tooltip="$t('common.share_image')">
                    <template v-slot:activator="{ on, attrs }">
                        <v-btn v-on="on" v-bind="attrs" icon small :loading="loadingShare" @click="shareImage">
                            <v-icon size="17">mdi-share-variant-outline</v-icon>
                        </v-btn>
                    </template>
                </button-tooltip>
            </template>

            <skeleton-loader v-if="loading || !report" type="rows" />

            <div v-else-if="!hasData" class="report-empty">
                <v-icon size="40" color="txtMuted">mdi-database-off-outline</v-icon>
                <p>{{ $t('common.no_values_student') }}</p>
            </div>

            <div v-else class="informe" data-share-target="evaluation-report">

                <!-- Header alumno + fecha -->
                <div class="inf-header">
                    <div class="inf-header-card">
                        <div class="inf-name">{{ student ? student.name : '' }}</div>
                        <div class="inf-date">{{ headerDate }}</div>
                    </div>
                    <div class="inf-header-logo">
                        <img v-if="logoUrl" :src="logoUrl" class="inf-logo-img" alt="" />
                        <v-icon v-else size="28" color="accent">mdi-chart-timeline-variant</v-icon>
                    </div>
                </div>

                <!-- Tabla de resultados -->
                <div class="inf-card">
                    <div class="inf-head inf-thead">
                        <span></span>
                        <span class="inf-th-right">{{ $t('common.actual') }}</span>
                        <span class="inf-th-right">{{ $t('common.diff_vs_ideal') }}</span>
                    </div>
                    <div class="inf-row" v-for="item in items" :key="item.id">
                        <span class="inf-row-label">{{ item.name }} <span class="inf-unit">({{ item.unit }})</span></span>
                        <span class="inf-row-val">{{ item.actual }}</span>
                        <span class="inf-row-diff" :class="item.color === 'success' ? 'good' : 'bad'">
                            {{ item.difference }}
                        </span>
                    </div>
                </div>

                <!-- Radar -->
                <div class="inf-card">
                    <div class="inf-head">{{ $t('common.performance_profile') }}</div>
                    <div class="inf-body">
                        <radar-chart :labels="report.evaluationsName" :datasets="report.evaluationData"
                            style="height: 230px;" />
                        <div class="inf-legend">
                            <div class="inf-legend-item"><span class="inf-dot dot-primary" />{{ $t('common.actual') }}</div>
                            <div class="inf-legend-item"><span class="inf-dot dot-accent" />{{ $t('common.ideal') }}</div>
                        </div>
                    </div>
                </div>

                <!-- Puntos fuertes -->
                <div v-if="report.strongPoints" class="inf-card">
                    <div class="inf-head inf-head--good">{{ $t('common.your_strong_points') }}</div>
                    <div class="inf-band-text">{{ report.strongPoints }}</div>
                </div>

                <!-- Enfócate en mejorar -->
                <div v-if="report.improvingPoints" class="inf-card">
                    <div class="inf-head inf-head--bad">{{ $t('common.improved_points') }}</div>
                    <div class="inf-band-text">{{ report.improvingPoints }}</div>
                </div>

                <!-- Comparativa barras -->
                <div class="inf-card">
                    <div class="inf-head">{{ $t('common.reference_comparison') }}</div>
                    <div class="inf-body">
                        <div class="inf-bars">
                            <div class="inf-bar-row" v-for="item in items" :key="item.id">
                                <div class="inf-bar-label">{{ item.name }}</div>
                                <div class="inf-bar-track">
                                    <div class="inf-bar-fill" :style="{ width: barPos(item, 'actual') }" />
                                    <div class="inf-bar-line line-low" :style="{ left: barPos(item, 'worst') }" />
                                    <div class="inf-bar-line line-mid" :style="{ left: barPos(item, 'average') }" />
                                    <div class="inf-bar-line line-opt" :style="{ left: barPos(item, 'best') }" />
                                </div>
                                <div class="inf-bar-val" :class="item.color === 'success' ? 'good-v' : 'bad-v'">
                                    {{ item.actual }}
                                </div>
                            </div>
                        </div>
                        <div class="inf-bars-legend">
                            <div class="inf-legend-item"><span class="inf-line-mark line-low" />{{ $t('common.threshold_low') }}</div>
                            <div class="inf-legend-item"><span class="inf-line-mark line-mid" />{{ $t('common.threshold_mid') }}</div>
                            <div class="inf-legend-item"><span class="inf-line-mark line-opt" />{{ $t('common.optimal') }}</div>
                            <div class="inf-legend-item"><span class="inf-fill-mark" />{{ $t('common.your_result') }}</div>
                        </div>
                    </div>
                </div>

                <!-- Comentario del entrenador -->
                <div v-if="report.comment" class="inf-card">
                    <div class="inf-head">{{ $t('common.coach_comment') }}</div>
                    <div class="inf-comment">{{ report.comment }}</div>
                </div>

                <!-- Marca BuilderPro -->
                <div class="inf-brand">
                    <img :src="require('@/assets/img/brand_mark.png')" class="inf-brand-logo" alt="" />
                    <span class="inf-brand-text">BuilderPro</span>
                </div>

            </div>
        </base-dialog>
    </div>
</template>

<script>
import RadarChart from './RadarChart.vue';
import BaseDialog from './BaseDialog';
import SkeletonLoader from './SkeletonLoader.vue';
import ButtonTooltip from './ButtonTooltip';
import { shareNodeAsImage } from '@/utils/ShareImage';

export default {
    name: "ViewReportStudent",
    components: { RadarChart, BaseDialog, SkeletonLoader, ButtonTooltip },
    data() {
        return {
            dialog: false,
            loading: false,
            loadingShare: false,
            student: null,
            report: null,
        }
    },
    computed: {
        items(vm = this) {
            return vm.report?.items || []
        },
        hasData(vm = this) {
            return vm.items.length > 0
        },
        canShare(vm = this) {
            return vm.accountType === 'coach' || vm.accountType === 'company'
        },
        headerDate(vm = this) {
            return vm.report?.date ? vm.$moment(vm.report.date).format('DD / MM / YYYY') : ''
        },
        logoUrl(vm = this) {
            const clean = (l) => (l && !l.includes('logo-placeholder')) ? l : null
            return clean(vm.report?.logo) || clean(vm.account && vm.account.logo && vm.account.logo.url) || clean(vm.brandLogo) || null
        },
    },
    methods: {
        async shareImage() {
            const vm = this
            if (vm.loadingShare) return
            if (!vm.requirePlan('shareImage')) return
            const node = document.querySelector('[data-share-target="evaluation-report"]')
            if (!node) return
            vm.loadingShare = true
            try {
                const title = vm.$t('common.evaluation_report')
                const date = vm.headerDate
                const name = vm.student?.name || ''
                await shareNodeAsImage(node, {
                    title,
                    period: date,
                    subject: name,
                    isDark: vm.$vuetify.theme.dark,
                    withLogo: false,
                    filenameParts: [title, date, name],
                    dialogTitle: vm.$t('common.share_image'),
                    loadingText: vm.$t('common.generating_image'),
                })
            } catch (err) {
                console.log('shareImage error', err)
            } finally {
                vm.loadingShare = false
            }
        },
        show(item = null) {
            const vm = this
            vm.setDefaults(item)
            vm.dialog = true
        },
        hide() {
            const vm = this
            vm.dialog = false
            vm.reset()
        },
        onInput(val) {
            if (!val) this.hide()
        },
        reset() {
            const vm = this
            vm.student = null
            vm.report = null
        },
        async setDefaults(item) {
            const vm = this;
            vm.loading = true
            vm.student = item.student
            vm.report = item.report
            vm.loading = false
        },
        barScale(item) {
            const nums = [item.actual, item.ideal, item.best, item.worst, item.average].map(Number).filter(n => !isNaN(n))
            if (!nums.length) return { lo: 0, span: 1 }
            let lo = Math.min(...nums), hi = Math.max(...nums)
            const pad = (hi - lo) * 0.12 || Math.abs(hi) * 0.12 || 1
            lo -= pad; hi += pad
            return { lo, span: (hi - lo) || 1 }
        },
        barPos(item, key) {
            const { lo, span } = this.barScale(item)
            const value = Number(item[key])
            if (isNaN(value)) return '0%'
            const p = Math.max(0, Math.min(100, ((value - lo) / span) * 100))
            return `${p}%`
        },
    },
}
</script>

<style scoped>
.report-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    padding: 48px 16px;
    color: var(--v-txtMuted-base);
    font-size: 14px;
    text-align: center;
}

.informe {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

/* Header */
.inf-header {
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 10px;
}

.inf-header-card {
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 10px;
    padding: 14px 16px;
}

.inf-name {
    font-size: 16px;
    font-weight: 700;
    color: var(--v-txtPrimary-base);
}

.inf-date {
    font-size: 12px;
    color: var(--v-txtSecondary-base);
    margin-top: 2px;
}

.inf-header-logo {
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 10px;
    padding: 6px 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 64px;
}

.inf-logo-img {
    height: 100%;
    max-height: 52px;
    min-height: 42px;
    max-width: 140px;
    object-fit: contain;
    border-radius: 8px;
}

/* Cards */
.inf-card {
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 10px;
    overflow: hidden;
}

/* Header uniforme */
.inf-head {
    height: 42px;
    display: flex;
    align-items: center;
    padding: 0 14px;
    border-bottom: 1px solid var(--v-outline-base);
    font-size: 12px;
    font-weight: 600;
    color: var(--v-txtSecondary-base);
}

.inf-head--good {
    background: rgba(204, 255, 51, 0.12);
    border-bottom-color: rgba(204, 255, 51, 0.25);
    color: var(--v-accent-base);
}

.theme--dark .inf-head--good {
    color: var(--v-primary-base);
}

.inf-head--bad {
    background: rgba(232, 92, 92, 0.08);
    border-bottom-color: rgba(232, 92, 92, 0.2);
    color: var(--v-error-base);
}

.inf-body {
    padding: 16px;
}

/* Tabla */
.inf-thead {
    display: grid;
    grid-template-columns: 1fr auto auto;
    gap: 8px;
    background: var(--v-surface2-base);
}

.inf-thead span {
    font-size: 11px;
    font-weight: 600;
    color: var(--v-txtMuted-base);
    text-transform: uppercase;
}

.inf-th-right {
    text-align: right;
}

.inf-row {
    display: grid;
    grid-template-columns: 1fr auto auto;
    align-items: center;
    gap: 8px;
    padding: 10px 14px;
    border-bottom: 1px solid var(--v-outline-base);
}

.inf-row:last-child {
    border-bottom: none;
}

.inf-row-label {
    font-size: 13px;
    font-weight: 500;
    color: var(--v-txtPrimary-base);
}

.inf-unit {
    font-size: 11px;
    color: var(--v-txtMuted-base);
}

.inf-row-val {
    font-size: 14px;
    font-weight: 700;
    color: var(--v-txtPrimary-base);
    text-align: right;
    min-width: 48px;
    padding-right: 12px;
}

.inf-row-diff {
    font-size: 13px;
    font-weight: 700;
    text-align: right;
    min-width: 58px;
}

.inf-row-diff.good {
    color: #22c55e;
}

.inf-row-diff.bad {
    color: #ef4444;
}

/* Bandas fuertes / mejorar */
.inf-band-text {
    padding: 12px 14px;
    font-size: 13px;
    color: var(--v-txtPrimary-base);
    line-height: 1.5;
    white-space: pre-line;
}

/* Barras */
.inf-bars {
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.inf-bar-row {
    display: flex;
    align-items: center;
    gap: 10px;
}

.inf-bar-label {
    font-size: 12px;
    color: var(--v-txtSecondary-base);
    width: 96px;
    flex-shrink: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.inf-bar-track {
    flex: 1;
    height: 28px;
    background: var(--v-surface2-base);
    border-radius: 4px;
    position: relative;
}

.inf-bar-fill {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    background: rgba(204, 255, 51, 0.18);
    border-radius: 4px 0 0 4px;
}

.theme--light .inf-bar-fill {
    background: rgba(51, 153, 153, 0.18);
}

.inf-bar-line {
    position: absolute;
    top: -4px;
    bottom: -4px;
    width: 2px;
    border-radius: 1px;
}

.inf-bar-val {
    font-size: 13px;
    font-weight: 700;
    width: 40px;
    text-align: right;
    flex-shrink: 0;
}

.inf-bar-val.good-v {
    color: #22c55e;
}

.inf-bar-val.bad-v {
    color: #ef4444;
}

.line-low {
    background: #ef4444;
}

.line-mid {
    background: #f59e0b;
}

.line-opt {
    background: #22c55e;
}

/* Comentario */
.inf-comment {
    padding: 12px 14px;
    font-size: 13px;
    color: var(--v-txtPrimary-base);
    line-height: 1.5;
    white-space: pre-line;
}

/* Marca */
.inf-brand {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 6px;
    padding-top: 4px;
    opacity: 0.5;
}

.inf-brand-logo {
    height: 20px;
    width: auto;
    object-fit: contain;
}

.inf-brand-text {
    font-size: 12px;
    font-weight: 700;
    color: var(--v-txtPrimary-base);
}

/* Legends */
.inf-legend {
    display: flex;
    justify-content: center;
    gap: 16px;
    margin-top: 8px;
}

.inf-bars-legend {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin-top: 14px;
}

.inf-legend-item {
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 11px;
    color: var(--v-txtMuted-base);
}

.inf-dot {
    width: 9px;
    height: 9px;
    border-radius: 2px;
    flex-shrink: 0;
}

.dot-primary {
    background: var(--v-primary-base);
}

.dot-accent {
    background: var(--v-accent-base);
}

.inf-line-mark {
    width: 2px;
    height: 12px;
    border-radius: 1px;
    flex-shrink: 0;
}

.inf-fill-mark {
    width: 10px;
    height: 10px;
    border-radius: 2px;
    background: rgba(204, 255, 51, 0.18);
    flex-shrink: 0;
}

.theme--light .inf-fill-mark {
    background: rgba(51, 153, 153, 0.18);
}
</style>
